import { useCallback, useEffect, useState } from 'react';

import { LENS_LAYOUT } from '~/configs/unifiedAnalyticsTheme';

import type { LensEditorPanelState } from '~/pages/LeLens/panel/panelTypes';

import type { ViewKey } from './viewState';

/** The user's standing choice, kept across visits. Absent means "let the shell decide". */
export const RAIL_COLLAPSED_STORAGE_KEY = 'askrichard.rail-collapsed';

/**
 * Below this many pixels of board, the rail gives way to the right pane. The inputs (viewport
 * and pane width) do not move when the rail collapses, so the rule cannot oscillate.
 */
export const RAIL_AUTO_COLLAPSE_MIN_CANVAS = 800;

function readStandingPreference(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  try {
    return window.localStorage.getItem(RAIL_COLLAPSED_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

function writeStandingPreference(collapsed: boolean) {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.setItem(RAIL_COLLAPSED_STORAGE_KEY, collapsed ? 'true' : 'false');
  } catch {
    // Private mode or blocked storage: the preference lasts for this visit only.
  }
}

export function railShouldAutoCollapse(args: {
  view: ViewKey;
  pane: LensEditorPanelState | null;
  viewportWidth: number;
  minCanvas?: number;
}): boolean {
  const { view, pane, viewportWidth, minCanvas = RAIL_AUTO_COLLAPSE_MIN_CANVAS } = args;
  if (view !== 'lens' || !pane?.open || pane.width <= 0 || viewportWidth <= 0) {
    return false;
  }
  return viewportWidth - pane.width - LENS_LAYOUT.railWidth < minCanvas;
}

export interface UseRailCollapsedArgs {
  view: ViewKey;
  /** Below md the rail is a drawer and never collapses. */
  isNarrow: boolean;
  pane: LensEditorPanelState | null;
  viewportWidth: number;
  minCanvas?: number;
}

/**
 * One boolean for the rail, from three inputs. While a pane is open, the toggle is a "give me
 * the rail back for now" switch that clears when the pane closes. With no pane open, the toggle
 * sets a standing preference that persists. The landing page starts expanded on each visit;
 * its toggle only applies until the reader leaves the landing page.
 */
export function useRailCollapsed({ view, isNarrow, pane, viewportWidth, minCanvas }: UseRailCollapsedArgs) {
  const [standingPreference, setStandingPreference] = useState(readStandingPreference);
  const [landingCollapsed, setLandingCollapsed] = useState(false);
  const [paneOverride, setPaneOverride] = useState<boolean | null>(null);
  const paneOpen = Boolean(pane?.open) && view === 'lens';

  useEffect(() => {
    if (view !== 'landing') {
      setLandingCollapsed(false);
    }
  }, [view]);

  useEffect(() => {
    if (!paneOpen) {
      setPaneOverride(null);
    }
  }, [paneOpen]);

  const autoCollapse = railShouldAutoCollapse({ view, pane, viewportWidth, minCanvas });
  const collapsed = isNarrow
    ? false
    : view === 'landing'
      ? landingCollapsed
      : paneOverride !== null
        ? paneOverride
        : standingPreference || autoCollapse;

  const toggle = useCallback(() => {
    if (view === 'landing') {
      setLandingCollapsed(!collapsed);
      return;
    }
    if (paneOpen) {
      setPaneOverride(!collapsed);
      return;
    }
    const next = !collapsed;
    setStandingPreference(next);
    writeStandingPreference(next);
  }, [collapsed, paneOpen, view]);

  return { collapsed, toggle, autoCollapse };
}

/** Tracks window.innerWidth. LeLensPage keeps its own copy for the pane clamp. */
export function useViewportWidth(): number {
  const [width, setWidth] = useState(() => (typeof window === 'undefined' ? 0 : window.innerWidth));
  useEffect(() => {
    const update = () => setWidth(window.innerWidth);
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);
  return width;
}
